// ===========================================
// Time Period Filter Component
// ===========================================
// Reusable filter buttons for date range selection across analytics views
// Matches the tactical terminal aesthetic of the app

import React from 'react';
import './TimePeriodFilter.scss';

// ===========================================
// Predefined Time Periods
// ===========================================
// Standard time periods for filtering analytics data
const TIME_PERIODS = [
  { key: 'this-week', label: 'This Week' },
  { key: 'this-month', label: 'This Month' },
  { key: 'last-month', label: 'Last Month' },
  { key: 'last-3-months', label: 'Last 3 Months' },
  { key: 'ytd', label: 'YTD' },
  { key: 'past-12-months', label: 'Past 12 Months' }
];

// ===========================================
// Date Range Calculator
// ===========================================
/**
 * Calculate date range from period key
 * @param {string} periodKey - One of the TIME_PERIODS keys
 * @returns {Object} { startDate: Date, endDate: Date }
 */
export const getDateRange = (periodKey) => {
  const now = new Date();
  // Create today at midnight for consistent date comparisons
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (periodKey) {
    case 'this-week': {
      // Start from Sunday of current week
      const dayOfWeek = today.getDay();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - dayOfWeek);
      return { startDate: startOfWeek, endDate: now };
    }
    
    case 'this-month': {
      // First day of current month
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: startOfMonth, endDate: now };
    }
    
    case 'last-month': {
      // First and last day of previous month
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      endOfLastMonth.setHours(23, 59, 59, 999);
      return { startDate: startOfLastMonth, endDate: endOfLastMonth };
    }
    
    case 'last-3-months': {
      // 3 months ago from the first of current month
      const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      return { startDate: start, endDate: now };
    }
    
    case 'ytd': {
      // January 1st of current year
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      return { startDate: startOfYear, endDate: now };
    }
    
    case 'past-12-months': {
      // Exactly 12 months ago
      const start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      return { startDate: start, endDate: now };
    }
    
    default:
      // Return null dates for "all time" view
      return { startDate: null, endDate: null };
  }
};

// ===========================================
// Time Period Filter Component
// ===========================================
/**
 * Renders a row of filter buttons for selecting time periods
 * @param {string} selectedPeriod - Currently selected period key
 * @param {function} onPeriodChange - Callback when period is changed
 */
const TimePeriodFilter = ({ selectedPeriod, onPeriodChange }) => {
  return (
    <div className="time-period-filter">
      {TIME_PERIODS.map(({ key, label }) => (
        <button
          key={key}
          className={`time-period-filter__btn ${
            selectedPeriod === key ? 'time-period-filter__btn--active' : ''
          }`}
          onClick={() => onPeriodChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

export default TimePeriodFilter;

